import React, { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { Box } from "@mui/material";
import { Button } from "../../../components/Button/Button";
import axiosInstant from "../../../api";
import ExpertGeneralInfo from "./ExpertGeneralInfo";
import IdealExpertExplanation from "./IdealExpertExplanation";
import ExpertSelectionCard from "./ExpertSelectionCard";
import { EmptyPanel } from "../../brand/projects/common/EmptyPanel";
import { IconExpert } from "../../../utils/icons";
import { BRAND_PROJECT_STATUSES, areExpertsApproved, sortExpertsBySelection, describeSharedSiblings, isProOptedIn } from "../helper";
import { isInternal } from "../../../utils";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import AnimatedReorderItem from "./AnimatedReorderItem";
import { DotLottleLoader } from "../../../components/Loader";
import { publicUpdateExpertStatus, publicUpdateProjectStatus } from "../../../api/endpoints";
import { useProjectExpertScores } from "../../../hooks/useProjectExpertScores";

interface Expert {
    userid: string;
    displayName?: string;
    avatar?: string;
    projects?: Array<{
        BrandProjectPro?: {
            score?: string;
            status?: number;
        };
    }>;
}

interface ExpertRankingProps {
    experts: Expert[];
    projectId: string;
    brandId: string;
    onUpdate: () => void;
    projectStatus?: string;
    project?: any;
    role?: string;
    loading?: boolean;
    /** When set, uses public token-based endpoints instead of private brand endpoints. */
    publicToken?: string;
    /**
     * The selection process id when it is SHARED across >1 project. Ranking is
     * process-wide, so the rank→Scheduling transition is fanned out to the
     * sibling projects (internal flow only). Null/undefined when not shared.
     */
    sharedProcessId?: number | null;
    /** The other projects on the shared process (for labelling). */
    sharedSiblingProjects?: { id: number; title?: string }[];
    publicBase?: string;
}

interface OrderState {
    [expertId: string]: number;
}

const ExpertRanking: React.FC<ExpertRankingProps> = ({
    experts,
    projectId,
    brandId,
    onUpdate,
    projectStatus,
    project,
    role,
    loading = false,
    publicToken,
    sharedProcessId = null,
    sharedSiblingProjects = [],
    publicBase = "brand-orders"
}) => {
    const [orders, setOrders] = useState<OrderState>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const isInternalUser = isInternal(role || "");

    // Only experts who opted in can be ranked (TDC-1633). The full roster is
    // kept in `experts` so a save can still clear a stale rank on an expert
    // who has since opted out or never answered.
    const rankableExperts = useMemo(() => (experts || []).filter(isProOptedIn), [experts]);

    useEffect(() => {
        const initialOrders: OrderState = {};
        if (rankableExperts.length > 0) {
            rankableExperts.forEach((expert) => {
                const currentOrder = expert.projects?.[0]?.BrandProjectPro?.status || 0;
                if (currentOrder > 0 && currentOrder < 999) {
                    initialOrders[expert.userid] = currentOrder;
                }
            });
        }
        setOrders(initialOrders);
    }, [rankableExperts]);

    const rankedIdsInOrder = useMemo(
        () =>
            Object.entries(orders)
                .filter(([, order]) => order > 0)
                .sort(([, a], [, b]) => a - b)
                .map(([id]) => id),
        [orders]
    );

    const visualOrders = useMemo(() => {
        const mapping: Record<string, number> = {};
        rankedIdsInOrder.forEach((id, index) => {
            mapping[id] = index + 1;
        });
        return mapping;
    }, [rankedIdsInOrder]);

    const sortedExperts = useMemo(() => {
        if (rankableExperts.length === 0) return [];

        const getScore = (expert: Expert): number => {
            const raw = expert.projects?.[0]?.BrandProjectPro?.score ?? "0";
            const parsed = parseFloat(String(raw));
            return Number.isFinite(parsed) ? parsed : 0;
        };

        return [...rankableExperts].sort((a, b) => {
            const visA = visualOrders[a.userid] || 0;
            const visB = visualOrders[b.userid] || 0;

            const aSelected = visA > 0;
            const bSelected = visB > 0;

            if (aSelected && bSelected) return visA - visB; // both selected: by visual rank
            if (aSelected) return -1; // selected first
            if (bSelected) return 1;

            return getScore(b) - getScore(a);
        });
    }, [rankableExperts, visualOrders]);

    const handleOrderChange = (expertId: string, order: number) => {
        if (!canEditRanking) {
            toast.error("You do not have permission to modify expert rankings. Please contact your account manager.");
            return;
        }

        setOrders((prev) => {
            const newOrders = { ...prev };

            if (order === -1) {
                const usedOrders = Object.values(newOrders).filter((o) => o > 0);
                const nextOrder = usedOrders.length > 0 ? Math.max(...usedOrders) + 1 : 1;
                newOrders[expertId] = nextOrder;
            } else if (order === 0) {
                const removedOrder = newOrders[expertId];
                newOrders[expertId] = 0;

                Object.keys(newOrders).forEach((id) => {
                    if (newOrders[id] > removedOrder) {
                        newOrders[id] -= 1;
                    }
                });
            } else {
                newOrders[expertId] = order;
            }
            return newOrders;
        });
    };

    const getSelectedExperts = () =>
        Object.entries(orders)
            .filter(([_, order]) => order > 0)
            .sort(([, a], [, b]) => a - b);

    const handleSubmit = () => {
        if (!canEditRanking) {
            toast.error("You do not have permission to modify expert rankings. Please contact your account manager.");
            return;
        }

        if (!publicToken && (!brandId || !projectId)) {
            toast.error("Missing project information. Please refresh the page and try again.");
            return;
        }

        const selectedExperts = getSelectedExperts();
        if (selectedExperts.length === 0) {
            toast.error("Please select at least one expert to rank.");
            return;
        }

        const isInitialRanking = !hasBeenRankedBefore;
        if (!isInitialRanking && !hasChanges) {
            toast.error("No changes detected. Please select and rank experts before saving.");
            return;
        }

        if (isInitialRanking) confirmSubmit();
        else setShowConfirmation(true);
    };

    const confirmSubmit = async () => {
        setShowConfirmation(false);
        setIsSubmitting(true);

        try {
            if (!publicToken && (!brandId || !projectId)) {
                toast.error("Missing project information. Please refresh the page and try again.");
                return;
            }

            const rankedIds = rankedIdsInOrder;

            if (rankedIds.length === 0) {
                toast.error("Please select at least one expert to rank.");
                return;
            }

            const numericProjectId = Number(projectId);

            const updatePromises = rankedIds.map((expertId, index) =>
                axiosInstant.patch(
                    publicToken
                        ? publicUpdateExpertStatus(publicToken, numericProjectId, Number(expertId), publicBase)
                        : `/brands/${brandId}/projects/${projectId}/pros/${expertId}/status`,
                    { status: index + 1 }
                )
            );

            // Deliberately the whole roster, not just the rankable experts:
            // anyone hidden from ranking (not opted in) loses any old rank.
            const unselectedExperts = (experts || []).filter(
                (expert) => !orders[expert.userid] || orders[expert.userid] === 0
            );

            const clearPromises = unselectedExperts.map((expert) =>
                axiosInstant.patch(
                    publicToken
                        ? publicUpdateExpertStatus(publicToken, numericProjectId, Number(expert.userid), publicBase)
                        : `/brands/${brandId}/projects/${projectId}/pros/${expert.userid}/status`,
                    { status: 999 }
                )
            );

            const allPromises = [...updatePromises, ...clearPromises];
            if (allPromises.length > 0) {
                await Promise.all(allPromises);
            }

            // Ranking is shared — the API fans the → Scheduling transition
            // out to the sibling projects on its own (both the authenticated
            // and the public-token endpoint).
            await axiosInstant.patch(
                publicToken
                    ? publicUpdateProjectStatus(publicToken, numericProjectId, publicBase)
                    : `/brands/${brandId}/projects/${projectId}`,
                {
                    status: BRAND_PROJECT_STATUSES.SCHEDULING
                }
            );

            // Success is a toast, not a modal — the user stays where they are
            // (this also fixes the public-token page, whose old modal
            // navigated an anonymous client to an internal route).
            toast.success(
                "Expert selection saved. Our team will confirm scheduling and availability next."
            );
            onUpdate();
        } catch (error: any) {
            console.error("Failed to update expert selection order:", error);
            toast.error("Failed to update expert selection order. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const hasChanges = useMemo(() => {
        const initialOrders: Record<string, number> = {};
        if (rankableExperts.length > 0) {
            rankableExperts.forEach((expert) => {
                const status = expert.projects?.[0]?.BrandProjectPro?.status;
                if (status && status > 0 && status < 999) {
                    // Only consider valid ranking status
                    initialOrders[expert.userid] = status;
                }
            });
        }

        return JSON.stringify(orders) !== JSON.stringify(initialOrders);
    }, [orders, rankableExperts]);

    const hasBeenRankedBefore = useMemo(() => {
        if (rankableExperts.length === 0) return false;
        return rankableExperts.some((expert) => {
            const status = expert.projects?.[0]?.BrandProjectPro?.status;
            return status && status > 0 && status < 999; // Valid ranking status (1-998)
        });
    }, [rankableExperts]);

    const canEditRanking = (!!publicToken || isInternalUser || !hasBeenRankedBefore) && !project?.process_locked;

    // Algorithm-suggested match scores per (expert, product). Skipped for
    // public-token pages — that endpoint is auth-only. Internal & brand users
    // see the per-product chips on each card alongside the human score.
    const {
        products: algoProducts,
        scoresByUserId: algoScoresByUserId,
        breakdownByUserId: algoBreakdownByUserId,
        industryScoreByUserId: algoIndustryScoreByUserId,
        expertTypeByUserId: algoExpertTypeByUserId,
        professionsByUserId: algoProfessionsByUserId,
        productIdealsByDesireId: algoProductIdealsByDesireId
    } = useProjectExpertScores(projectId ? Number(projectId) : undefined, { skip: !!publicToken });

    const getProfessionNames = (brief: any) => {
        if (!brief) return "N/A";

        if (brief.professions && brief.professions.length > 0) {
            const professionNames = brief.professions.map((p: any) => p.profession?.name).filter(Boolean);
            return professionNames.length > 0 ? professionNames.join(", ") : "N/A";
        }

        return brief.profession?.name || "N/A";
    };

    const hasNeverBeenSubmitted =
        projectStatus === BRAND_PROJECT_STATUSES.EXPERT_PROPOSALS_NEEDED ||
        projectStatus === BRAND_PROJECT_STATUSES.PENDING ||
        projectStatus === BRAND_PROJECT_STATUSES.BRIEF_PENDING ||
        projectStatus === BRAND_PROJECT_STATUSES.BRIEF_REVIEW_NEEDED;

    const expertsNotYetApproved =
        projectStatus === BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED ||
        projectStatus === BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_DENIED;

    if (loading) {
        return (
            <Box className="flex justify-center items-center h-full my-12">
                <DotLottleLoader />
            </Box>
        );
    }

    if (hasNeverBeenSubmitted || expertsNotYetApproved || !experts || experts.length === 0) {
        return (
            <Box className="flex justify-center items-center h-full my-12">
                <EmptyPanel
                    icon={<IconExpert stroke="#AB854A" />}
                    showSpacing
                    text="No experts yet. You will be notified when experts are available for selection."
                />
            </Box>
        );
    }

    if (rankableExperts.length === 0) {
        return (
            <Box className="flex justify-center items-center h-full my-12">
                <EmptyPanel
                    icon={<IconExpert stroke="#AB854A" />}
                    showSpacing
                    text="No experts have opted in yet. Only experts who opt in can be ranked — you will be notified when they are ready for selection."
                />
            </Box>
        );
    }

    return (
        <div className="flex flex-col gap-[24px] pb-[40px]">
            <div className="flex flex-wrap items-start gap-x-8 gap-y-4 sm:gap-[48px] px-[24px]">
                <ExpertGeneralInfo title="Gender" description={project?.briefs?.[0]?.expert_gender || "N/A"} />
                <ExpertGeneralInfo
                    title="Age Range"
                    description={
                        project?.briefs?.[0]?.expert_age_min && project?.briefs?.[0]?.expert_age_max
                            ? `${project.briefs[0].expert_age_min}–${project.briefs[0].expert_age_max}`
                            : "N/A"
                    }
                />
                <ExpertGeneralInfo
                    title="Profession / Expertise"
                    description={getProfessionNames(project?.briefs?.[0])}
                />
                <IdealExpertExplanation
                    products={algoProducts}
                    idealsByDesireId={algoProductIdealsByDesireId}
                    brandId={brandId}
                />
            </div>

            {/* Same band, same reason as expertSelection/index.tsx: --surface-2
                #1F2A3B is BRIGHTER than the --surface #18212F cards it holds, so
                in dark the cards punch holes in it. Correct in light (cream
                behind white reads as recessed), so the removal is dark-gated. */}
            <div className="bg-surface-2 dark:bg-transparent p-[24px]">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="text-text-1 text-[24px] font-black font-[CircularStd]">
                        Expert Selection ({rankableExperts.length})
                        {!!sharedProcessId && sharedSiblingProjects.length > 0 && (
                            <span className="ml-2 text-[13px] font-medium text-text-2 align-middle">
                                · ranking shared with {describeSharedSiblings(sharedSiblingProjects)}
                            </span>
                        )}
                    </div>
                    {canEditRanking && (
                        <Button
                            onClick={handleSubmit}
                            loading={isSubmitting}
                            disabled={!hasChanges || isSubmitting}
                            className={!hasChanges || isSubmitting ? "opacity-50 cursor-not-allowed" : ""}
                        >
                            Save Selection
                        </Button>
                    )}
                </div>

                <div className="mt-2 text-[13px] font-medium text-text-2">
                    Only experts who opted in to this project are shown
                    {rankableExperts.length < experts.length
                        ? ` (${rankableExperts.length} of ${experts.length} proposed).`
                        : "."}
                </div>

                <Box className="overflow-auto w-full mt-4">
                    <Box className="flex gap-4 min-w-fit [&>div]:w-[300px] [&>div]:shrink-0">
                        {sortedExperts.map((expert, expertIndex) => (
                            <AnimatedReorderItem key={expert.userid} orderKey={expertIndex}>
                                <ExpertSelectionCard
                                    pro={expert}
                                    isSelected
                                    recommendationCheck
                                    disableSelection={!canEditRanking}
                                    onUpdate={onUpdate}
                                    project={project}
                                    onScoreChange={() => {}}
                                    approvalCheck={false}
                                    isTopRecommendation={false}
                                    isApprovalPage={false}
                                    currentOrder={visualOrders[expert.userid] || 0}
                                    onOrderChange={canEditRanking ? handleOrderChange : undefined}
                                    isRankingMode
                                    canEditRanking={canEditRanking}
                                    forceHideOptStatus={!!publicToken}
                                    forceHideRate={!!publicToken}
                                    publicToken={publicToken as any}
                                    algoProducts={algoProducts}
                                    algoScores={algoScoresByUserId[String(expert.userid)]}
                                    algoBreakdown={algoBreakdownByUserId[String(expert.userid)]}
                                    algoIndustryScores={algoIndustryScoreByUserId[String(expert.userid)]}
                                    algoExpertTypes={algoExpertTypeByUserId[String(expert.userid)]}
                                    algoProfessions={algoProfessionsByUserId[String(expert.userid)]}
                                    sharedSiblingProjects={sharedSiblingProjects}
                                />
                            </AnimatedReorderItem>
                        ))}
                    </Box>
                </Box>
            </div>

            {canEditRanking && (
                <ConfirmationDialog
                    open={showConfirmation}
                    onClose={() => setShowConfirmation(false)}
                    onConfirm={confirmSubmit}
                    title="Expert Selection"
                    description={
                        hasBeenRankedBefore
                            ? "Are you sure you want to re-submit these expert ranks?"
                            : "Are you sure you want to submit these expert ranks?"
                    }
                    confirmLabel={hasBeenRankedBefore ? "Re-submit" : "Submit"}
                    cancelLabel="Cancel"
                />
            )}

        </div>
    );
};

export default ExpertRanking;
